/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { formatRelativeDayTime } from '@shared/vueltoDateTime';
import type { CashVm } from './cashManagement.types';
import {
  formatCashDifferenceInline,
  formatCashDrawerBreakdown,
  formatTurnoResumenSubtitle,
} from './cashManagement.utils';
import { cashStyles } from './cashManagement.styles';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { CashCollapsibleSection } from '../CashCollapsibleSection';
import { CashTurnoSummaryBody } from './CashTurnoSummaryBody';
import { cashArqueoCopy } from './cashArqueoCopy';
import { formatTurnoResumenTitle } from './cashCloseTurnoPresentation';
import { CashOpenTurnoForm } from './CashOpenTurnoForm';

type Props = {
  vm: CashVm;
  onOpenLastClosed: () => void;
  onOpenOutflows: () => void;
  onOpenCloseCash: () => void;
  onRegisterOutflow: () => void;
  onRegisterInflow?: () => void;
  onOpenActivity?: () => void;
};

/** Cuerpo mobile bajo `CashRecordHeader` — desglose, abrir turno, enlaces. */
export function CashTurnoHeroPanel({
  vm,
  onOpenLastClosed,
  onOpenOutflows,
  onOpenCloseCash,
  onRegisterOutflow,
  onRegisterInflow,
  onOpenActivity,
}: Props) {
  const navigation = useNavigation();
  const { layoutWebShell } = useContentLayout('dataWide');
  const mobile = !layoutWebShell;
  const activeSession = vm.cashSummary?.activeSession;
  const openingAmount = activeSession?.openingAmount ?? 0;
  const sessionCashTotal = vm.cashSummary?.sessionCashTotal ?? 0;
  const sessionInflowsTotal = vm.cashSummary?.sessionInflowsTotal ?? 0;
  const sessionCashSalesTotal = sessionCashTotal - sessionInflowsTotal;
  const sessionOutflowsTotal = vm.cashSummary?.sessionOutflowsTotal ?? 0;
  const sessionSalesCount = vm.cashSummary?.sessionSalesCount ?? 0;
  const sessionSalesTotal = vm.cashSummary?.sessionSalesTotal ?? 0;
  const todaySalesCount = vm.cashSummary?.todaySalesCount ?? 0;
  const resumenTitle = formatTurnoResumenTitle(activeSession?.openedAt);
  const lastClosed = vm.cashSummary?.lastClosedSession;
  const lastClosedDiff = lastClosed?.difference ?? 0;

  if (activeSession) {
    return (
      <View style={cashStyles.turnoMobileBody}>
        {/* Desglose band */}
        {vm.expectedCashInDrawer != null ? (
          <View style={[panelStyles.cashHeroBand, mobile && panelStyles.cashHeroBandMobile]}>
            <Text style={[panelStyles.cashHeroKicker, mobile && panelStyles.cashHeroKickerMobile]}>Desglose efectivo</Text>
            <Text style={[panelStyles.cashHeroSubtitle, mobile && panelStyles.cashHeroSubtitleMobile]}>
              {formatCashDrawerBreakdown(openingAmount, sessionCashSalesTotal, {
                outflowsTotal: sessionOutflowsTotal,
                showOutflows: vm.cashOutflowsEnabled,
                inflowsTotal: sessionInflowsTotal,
                showInflows: vm.cashOutflowsEnabled,
              })}
            </Text>
            {vm.cashOutflowsEnabled ? (
              <TouchableOpacity onPress={onOpenOutflows} accessibilityRole="button" accessibilityLabel="Ver egresos">
                <Text style={[panelStyles.hintLink, mobile && panelStyles.cashHeroLinkMobile]}>Ver egresos</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        {/* Actions row (after desglose, before resumen) */}
        {mobile ? (
          <View style={cashStyles.recordMobileActions}>
            {vm.cashOutflowsEnabled && onRegisterInflow ? (
              <TouchableOpacity
                style={cashStyles.btnMobileSecondary}
                onPress={onRegisterInflow}
                disabled={!vm.canEdit}
                accessibilityRole="button"
                accessibilityLabel={cashArqueoCopy.registerInflowCta}
                testID="caja.inflow.open"
              >
                <Text style={cashStyles.btnMobileSecondaryText}>{cashArqueoCopy.registerInflowCta}</Text>
              </TouchableOpacity>
            ) : null}
            {vm.cashOutflowsEnabled ? (
              <TouchableOpacity
                style={cashStyles.btnMobileSecondary}
                onPress={onRegisterOutflow}
                disabled={!vm.canEdit}
                accessibilityRole="button"
                accessibilityLabel={cashArqueoCopy.registerOutflowCta}
              >
                <Text style={cashStyles.btnMobileSecondaryText}>{cashArqueoCopy.registerOutflowCta}</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={[cashStyles.btnMobilePrimary, (!vm.canEdit || vm.closingCash) && styles.disabled]}
              onPress={onOpenCloseCash}
              disabled={!vm.canEdit}
              accessibilityRole="button"
              accessibilityLabel={cashArqueoCopy.closeTurnoCta}
              testID="caja.close.submit"
            >
              {vm.closingCash ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={cashStyles.btnMobilePrimaryText}>{cashArqueoCopy.closeTurnoCta}</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Resumen del turno — info-panel (mobile) / collapsible (wide) */}
        {mobile ? (
          <View style={cashStyles.infoPanelMobile}>
            <Text style={cashStyles.infoPanelMobileTitle}>{resumenTitle}</Text>
            <Text style={cashStyles.infoPanelMobileSubtitle}>
              {formatTurnoResumenSubtitle(sessionSalesCount, sessionSalesTotal, todaySalesCount)}
            </Text>
            <CashTurnoSummaryBody vm={vm} />
          </View>
        ) : (
          <CashCollapsibleSection
            title={resumenTitle}
            subtitle={formatTurnoResumenSubtitle(sessionSalesCount, sessionSalesTotal, todaySalesCount)}
            defaultExpanded={false}
          >
            <CashTurnoSummaryBody vm={vm} />
          </CashCollapsibleSection>
        )}

        {/* Último cierre link */}
        {lastClosed ? (
          <TouchableOpacity
            style={mobile ? cashStyles.linkMobileRow : cashStyles.linkRow}
            onPress={onOpenLastClosed}
            accessibilityRole="button"
            accessibilityLabel="Último cierre"
          >
            <View style={cashStyles.linkTextCol}>
              <Text style={cashStyles.linkTitle}>Último cierre</Text>
              <Text style={cashStyles.linkSubtitle} numberOfLines={1}>
                {lastClosed.closedAt ? formatRelativeDayTime(lastClosed.closedAt) : '—'}
                {formatCashDifferenceInline(lastClosedDiff)}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
          </TouchableOpacity>
        ) : null}

        {/* Actividad link */}
        {onOpenActivity ? (
          <TouchableOpacity
            style={mobile ? cashStyles.linkMobileRow : cashStyles.linkRow}
            onPress={onOpenActivity}
            accessibilityRole="button"
            accessibilityLabel="Actividad de caja"
            testID="caja.activity.open"
          >
            <View style={cashStyles.linkTextCol}>
              <Text style={cashStyles.linkTitle}>Actividad</Text>
              <Text style={cashStyles.linkSubtitle} numberOfLines={1}>
                Movimientos de caja de la sucursal
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  return (
    <View style={cashStyles.turnoMobileBody}>
      <CashOpenTurnoForm
        vm={vm}
        footer={
          vm.cashOutflowsEnabled && vm.expensesModuleEnabled ? (
            <View style={panelStyles.closedCashOutflowsHint}>
              <Text style={styles.caption}>{cashArqueoCopy.closedCashExpensesHint}</Text>
              <TouchableOpacity
                onPress={() => (navigation as NavigationProp<ParamListBase>).navigate('Expenses')}
                accessibilityRole="button"
              >
                <Text style={panelStyles.hintLink}>Ir a Gastos</Text>
              </TouchableOpacity>
            </View>
          ) : null
        }
      />

      <CashTurnoSummaryBody vm={vm} />
    </View>
  );
}
